﻿/**
 * Attach URL query param to an existing url. 
 * 
 * @param url
 * @returns {*|string}
 */
export function withQueryParams(url) {
    const currentParams = new URLSearchParams(window.location.search);

    const u = new URL(url, window.location.origin);

    const allowed = ["utm_source", "utm_medium", "utm_campaign", "lang"];

    allowed.forEach(key => {
        const value = currentParams.get(key);
        if (value) {
            u.searchParams.set(key, value);
        }
    });

    return u.pathname + u.search;
}

/**
 * Enables comma-decimal (",") support for jQuery & ASP.NET Core Unobtrusive Validation.
 *
 * --- CONTEXT & PROBLEM ---
 * ASP.NET Core MVC/Razor Pages Unobtrusive Validation attaches native jQuery Validation rules
 * (such as `number`, `min`, `max`, and `range`) to form fields based on C# model metadata or HTML5 attributes
 * (e.g., `max="70"`, `inputmode="decimal"`).
 *
 * By default, jQuery Validation expects standard dot-decimal notation ("1.5").
 * When client-side sanitizers convert user input from dot to comma ("1,5"), standard jQuery validation
 * fails internally during `parseFloat()` checks, resulting in unexpected `input-validation-error`
 * classes and `aria-invalid="true"` states—even on string-bound model properties with range attributes.
 *
 * --- SOLUTION ---
 * This method overrides jQuery Validation's internal evaluation methods (`number`, `min`, `max`, `range`).
 * It normalizes comma-decimal strings ("1,5") to dot-decimal strings ("1.5") before performing
 * numerical evaluations, preserving full client-side validation functionality without breaking on commas.
 * 
 * NOTE! 
 * This method is used currently on rateCreate form for packageWeightField but could be also for other fields.
 *
 * @dependency jQuery (window.jQuery)
 * @dependency jQuery Validation ($.validator)
 */
export function enableCommaDecimalValidation() {
    if (!window.jQuery || !$.validator) {
        return;
    }

    // 1. Accept numbers with either dot (.) or comma (,) as valid decimals
    $.validator.methods.number = function (value, element) {
        return this.optional(element) || /^-?\d+(?:[.,]\d+)?$/.test(value);
    };

    // 2. Normalize comma to dot before evaluating 'max' rule
    $.validator.methods.max = function (value, element, param) {
        if (this.optional(element)) return true;
        
        const normalized = String(value).replace(',', '.');
        
        return !isNaN(normalized) && Number(normalized) <= param;
    };

    // 3. Normalize comma to dot before evaluating 'min' rule
    $.validator.methods.min = function (value, element, param) {
        if (this.optional(element)) return true;
        
        const normalized = String(value).replace(',', '.');
        
        return !isNaN(normalized) && Number(normalized) >= param;
    };

    // 4. Normalize comma to dot before evaluating 'range' rule (min to max)
    $.validator.methods.range = function (value, element, param) {
        if (this.optional(element)) return true;
        
        const normalized = String(value).replace(',', '.');
        
        return !isNaN(normalized) && Number(normalized) >= param[0] && Number(normalized) <= param[1];
    };
}
